'use client';
/**
 * scrolltop1(2026-10-18) — 외부 유입 첫 로드 맨 위 가드. 아무것도 그리지 않는다(null) — SSR HTML 불변(크롤러·사용자 동일).
 *
 * 왜·규칙: lib/scroll/first-load-top.ts 머리말. 요약 —
 *   navigate 첫 로드 · 해시 없음 · 사용자 입력 전 → scrollY>0 이면 scrollTo(0,0).
 *   하이드레이션 직후 · 다음 프레임 · load · (감시 창 안의) 사용자 아닌 scroll 이벤트마다 검사한다.
 *   감시 창 = load 뒤 GUARD_AFTER_LOAD_MS, 최대 GUARD_MAX_MS. 사용자 입력·해시 생김·경로 바뀜이면 즉시 끝.
 *   reload/back_forward 는 손대지 않는다(history.scrollRestoration 도 건드리지 않는다 — 브라우저 복원 그대로).
 * ⚠️ html{scroll-behavior:smooth}(globals.css) 때문에 그냥 scrollTo 하면 «미끄러져» 올라간다 → 그 순간만 auto.
 * ⛔ 여기서 focus·scrollIntoView 금지.
 * 🤝 리드폼 바텀시트(모바일 관심고객 버튼) 호환 — 시트는 «탭» 으로만 열리고, 그 탭(touchstart/pointerdown)이 이 가드를 즉시 끈다.
 *    그러니 시트의 스크롤 잠금(body fixed + top:-Y → 닫을 때 scrollTo(0,Y))을 가드가 되돌리는 일은 없다. 시트를 «로드 시 자동» 으로 열지 말 것.
 */
import { useEffect } from 'react';
import {
  GUARD_AFTER_LOAD_MS, GUARD_MAX_MS, USER_ACTED_FLAG, USER_INPUT_EVENTS,
  navTypeOf, shouldArmFirstLoadTop, shouldResetNow,
} from '@/lib/scroll/first-load-top';

// 문서당 1회(클라이언트 이동·재마운트에는 다시 켜지지 않는다).
let armedOnce = false;

export default function FirstLoadScrollTop() {
  useEffect(() => {
    if (armedOnce) return;
    armedOnce = true;
    if (!shouldArmFirstLoadTop(navTypeOf(typeof performance !== 'undefined' ? performance : null), window.location.hash)) return;

    const w = window as unknown as Record<string, unknown>;
    const url0 = window.location.pathname + window.location.search;
    const timers: number[] = [];
    let raf = 0;
    let done = false;

    const userActed = () => w[USER_ACTED_FLAG] === 1;
    const onInput = () => { w[USER_ACTED_FLAG] = 1; stop(); };
    const check = () => {
      if (done) return;
      const sameUrl = window.location.pathname + window.location.search === url0;
      if (userActed() || !sameUrl || (window.location.hash && window.location.hash !== '#')) { stop(); return; }
      if (!shouldResetNow({ scrollY: window.scrollY, userActed: false, hash: window.location.hash, sameUrl })) return;
      const st = document.documentElement.style;
      const prev = st.scrollBehavior;
      st.scrollBehavior = 'auto';
      try { window.scrollTo(0, 0); } catch { /* 무시 */ }
      st.scrollBehavior = prev;
    };
    const onLoad = () => { check(); timers.push(window.setTimeout(stop, GUARD_AFTER_LOAD_MS)); };
    function stop() {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', check);
      window.removeEventListener('load', onLoad);
      for (const t of USER_INPUT_EVENTS) window.removeEventListener(t, onInput, true);
      for (const t of timers) window.clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
    }

    for (const t of USER_INPUT_EVENTS) window.addEventListener(t, onInput, { capture: true, passive: true });
    window.addEventListener('scroll', check, { passive: true });
    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
    timers.push(window.setTimeout(stop, GUARD_MAX_MS));
    check();
    raf = requestAnimationFrame(() => { raf = 0; check(); });
    // 정리 함수는 두지 않는다 — 루트 layout 은 문서 수명 동안 언마운트되지 않고, 감시는 stop() 이 스스로 끝낸다
    //   (StrictMode 이중 실행에서 정리→재실행이 armedOnce 에 막혀 개발 모드 가드가 죽는 것도 막는다).
  }, []);
  return null;
}
